import React, { useCallback, useEffect, useMemo, useState } from 'react'
import PropTypes from 'prop-types'
import { Box, CircularProgress, TextField, Typography } from '@mui/material'
import { addDays, isAfter, isBefore, parseISO, subDays } from 'date-fns'
import { NO_DATA_MESSAGE } from '../../utils/constants'
import PriceHistoryPagination from './PriceHistoryPagination'
import PriceHistorySearchTable from './PriceHistorySearchTable'
import loadable from '@loadable/component'
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFnsV3'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'

const PAGE_SIZE = 30
const containerStyle = { display: 'flex', gap: '24px', mt: 4, mb: 4 }
const labelStyle = { '& .MuiFormLabel-root': { color: 'rgb(9, 42, 72)' } }
const LoadableDatePicker = loadable(
  () =>
    import('@mui/x-date-pickers/DatePicker').then(
      (module) => module.DatePicker
    ),
  {
    fallback: <CircularProgress size={24} />,
    ssr: false,
  }
)

const PriceHistorySearchForm = ({ data }) => {
  const priceHistory = useMemo(() => {
    return (
      data?.priceHistory
        ?.map((item) => ({
          ...item,
          date: parseISO(item.date),
        }))
        ?.filter((item) => !isNaN(item.date)) || []
    )
  }, [data])

  const minDate = useMemo(
    () => (priceHistory.length > 0 ? priceHistory[0].date : new Date()),
    [priceHistory]
  )
  const maxDate = useMemo(
    () =>
      priceHistory.length > 0
        ? priceHistory[priceHistory.length - 1].date
        : new Date(),
    [priceHistory]
  )

  const initialData = useMemo(
    () => priceHistory.slice(-PAGE_SIZE),
    [priceHistory]
  )

  const initialDates = useMemo(() => {
    let dateFrom =
      initialData.length > 0 ? initialData[0].date : subDays(new Date(), 30)
    let dateTo = maxDate
    dateFrom = addDays(dateFrom, 1)
    dateTo = addDays(dateTo, 1)
    return { dateFrom, dateTo }
  }, [initialData, maxDate])

  const [selectedDateFrom, setSelectedDateFrom] = useState(
    initialDates.dateFrom
  )
  const [selectedDateTo, setSelectedDateTo] = useState(initialDates.dateTo)
  const [filteredData, setFilteredData] = useState([])
  const [pageData, setPageData] = useState([])
  const [pageCount, setPageCount] = useState(1)
  const [pageNumber, setPageNumber] = useState(1)

  useEffect(() => {
    const filtered = priceHistory.filter(
      (item) =>
        isAfter(item.date, subDays(selectedDateFrom, 1)) &&
        isBefore(item.date, subDays(selectedDateTo, 1))
    )
    const totalItems = filtered.length
    const newPageCount = totalItems > 0 ? Math.ceil(totalItems / PAGE_SIZE) : 1
    setFilteredData(filtered)
    setPageData(filtered.slice(0, PAGE_SIZE))
    setPageCount(newPageCount)
    setPageNumber(1)
  }, [selectedDateFrom, selectedDateTo, priceHistory])

  const handlePageChange = useCallback(
    (paginator) => {
      const newPageNumber = paginator.selected + 1
      const start = (newPageNumber - 1) * PAGE_SIZE
      setPageData(filteredData.slice(start, start + PAGE_SIZE))
      setPageNumber(newPageNumber)
    },
    [filteredData]
  )

  const handleFromDateChange = useCallback((newValue) => {
    if (newValue) {
      setSelectedDateFrom(newValue)
    }
  }, [])

  const handleToDateChange = useCallback((newValue) => {
    if (newValue) {
      setSelectedDateTo(newValue)
    }
  }, [])

  const renderInput = useCallback((params) => <TextField {...params} />, [])

  if (priceHistory.length === 0) {
    return <Typography>{NO_DATA_MESSAGE}</Typography>
  }

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Box>
        <Box sx={containerStyle}>
          <LoadableDatePicker
            label="From Date"
            value={selectedDateFrom}
            minDate={minDate}
            maxDate={selectedDateTo}
            onChange={handleFromDateChange}
            renderInput={renderInput}
            sx={labelStyle}
          />
          <LoadableDatePicker
            label="To Date"
            value={selectedDateTo}
            minDate={selectedDateFrom}
            maxDate={maxDate}
            onChange={handleToDateChange}
            renderInput={renderInput}
          />
        </Box>
        {filteredData.length === 0 ? (
          <Typography>No data in selected date range</Typography>
        ) : (
          <Box>
            <PriceHistorySearchTable page={pageData} />
            <PriceHistoryPagination
              onPageChange={handlePageChange}
              pageCount={pageCount}
            />
          </Box>
        )}
      </Box>
    </LocalizationProvider>
  )
}

PriceHistorySearchForm.propTypes = {
  data: PropTypes.shape({
    priceHistory: PropTypes.arrayOf(
      PropTypes.shape({
        date: PropTypes.string,
        nav: PropTypes.number,
      })
    ),
    name: PropTypes.string,
  }),
}

export default PriceHistorySearchForm
